import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';

const API = `${process.env.REACT_APP_API_URL}/discord-webhook`;

const SOURCE_CHIP = {
  db: { label: 'ตั้งค่าในระบบ', color: 'success' },
  env: { label: 'ใช้ค่าเดิมจาก .env', color: 'info' },
  none: { label: 'ยังไม่ตั้งค่า', color: 'default' },
};

// /management/discord — จัดการลิงก์ Discord webhook ของห้องที่ระบบส่งข้อความเข้า (เช็คอิน / รายงานประจำวัน / แจ้งเตือนระบบ)
// ลิงก์ที่ตั้งผ่านหน้านี้เข้ารหัสเก็บใน DB และมีผลทันที (ไม่ต้องแก้ .env/รีสตาร์ท) ลบแล้วกลับไปใช้ค่า .env เดิม (ถ้ามี)
// ลิงก์เต็มไม่ถูกส่งกลับมาแสดงอีกหลังบันทึก (เห็นแค่ส่วนที่ถูก mask) — ใครได้ลิงก์เต็มก็โพสต์เข้าห้องได้
export default function DiscordWebhooks({ title }) {
  const [rows, setRows] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [url, setUrl] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(API);
    setRows(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key, fn, okMsg) => {
    setBusyKey(key);
    try {
      const res = await fn();
      if (res?.data && Array.isArray(res.data)) setRows(res.data);
      if (okMsg) showAlert(okMsg);
    } finally {
      setBusyKey(null);
    }
  };

  const handleSave = async () => {
    // ช่องว่าง + มีลิงก์ในระบบอยู่ = ล้างลิงก์ กลับไปใช้ค่า .env เดิม / ช่องว่าง + ยังไม่มีลิงก์ในระบบ = ไม่มีอะไรให้บันทึก
    if (!url.trim() && editRow.source !== 'db') {
      showAlert('กรุณาวางลิงก์ Discord webhook');
      return;
    }
    const key = editRow.key;
    await run(key, () => api.put(`${API}/${key}`, { url: url.trim() }), 'บันทึกสำเร็จ');
    setEditRow(null);
    setUrl('');
  };

  const handleToggle = (row) =>
    run(row.key, () => api.put(`${API}/${row.key}`, { enabled: !row.enabled }));

  const handleTest = (row) =>
    run(row.key, () => api.post(`${API}/${row.key}/test`), 'ส่งข้อความทดสอบแล้ว — ตรวจดูในห้อง Discord');

  const handleRemove = async (row) => {
    if (!(await confirmAsync(`ลบลิงก์ "${row.label}" ที่ตั้งในระบบ? (จะกลับไปใช้ค่า .env เดิมถ้ามี)`))) return;
    run(row.key, () => api.delete(`${API}/${row.key}`));
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        ตั้งลิงก์ Discord webhook ของแต่ละห้องที่ระบบส่งข้อความเข้า (สร้างลิงก์ได้ที่ Discord → ตั้งค่าห้อง → Integrations → Webhooks)
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ห้อง</TableCell>
                <TableCell>สถานะลิงก์</TableCell>
                <TableCell>ลิงก์ (ซ่อนบางส่วน)</TableCell>
                <TableCell align="center">เปิดส่ง</TableCell>
                <TableCell align="center">จัดการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const chip = SOURCE_CHIP[row.source] || SOURCE_CHIP.none;
                const hasUrl = row.source !== 'none';
                return (
                  <TableRow key={row.key}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" color={chip.color} label={chip.label} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', color: '#666' }}>{row.masked_preview || '-'}</TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={Boolean(row.enabled)}
                        // ค่าจาก .env ปิดจากหน้านี้ไม่ได้ — ต้องตั้งลิงก์ในระบบก่อนถึงจะสลับเปิด/ปิดได้
                        disabled={row.source !== 'db' || busyKey === row.key}
                        onChange={() => handleToggle(row)}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 260 }}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busyKey === row.key}
                          onClick={() => { setEditRow(row); setUrl(''); }}
                        >
                          {row.source === 'db' ? 'แก้ไขลิงก์' : 'ตั้งลิงก์'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={!hasUrl || !row.enabled || busyKey === row.key}
                          onClick={() => handleTest(row)}
                        >
                          ทดสอบส่ง
                        </Button>
                        {row.source === 'db' && (
                          <Button
                            size="small"
                            color="error"
                            disabled={busyKey === row.key}
                            onClick={() => handleRemove(row)}
                          >
                            ลบ
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={Boolean(editRow)} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRow?.label}</DialogTitle>
        <DialogContent>
          {editRow?.masked_preview && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              ลิงก์ปัจจุบัน: {editRow.masked_preview} (วางลิงก์ใหม่เพื่อแทนที่ — เว้นว่างแล้วบันทึก = ล้างลิงก์ กลับไปใช้ค่าจาก .env)
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            label="Discord webhook URL"
            placeholder="https://discord.com/api/webhooks/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>ยกเลิก</Button>
          <Button variant="contained" onClick={handleSave}>บันทึก</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
